import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import React, {ReactNode} from "react";
import Col from "react-bootstrap/Col";
import {MANDATORY_ICON} from "../FormComponents";
import {ResearcherInterestsListing} from "../../../util/ResearcherInterestsUtils";
import {Button} from "react-bootstrap";
import {FieldError, get, Path, PathValue, useFormContext} from "react-hook-form";

/**
 * Props for this component
 */
interface ResearcherInterestsProps<T extends Record<string, any>> {
  label: string;
  helpText?: ReactNode;
  mandatory?: boolean;
  interestsInSection: ResearcherInterestsListing[];
  fieldId: Path<T>;
}

/**
 * This component will add to the form a researcher interest section.
 *
 * @param label The label that will show as the title of the section (Will show to the left of the
 * selection box)
 * @param helpText Subtext that shows on top of the question
 * @param mandatory <i>Optional</i> A boolean to mark the field as mandatory
 * @param interestsInSection An array of {@link ResearcherInterestsListing} to populate the section
 * with the relevant interests
 * @param fieldId The ID of the field as defined in the form interface
 */
export default function ResearcherInterestsSection<T extends Record<string, any>>({
                                                                                    label,
                                                                                    helpText,
                                                                                    mandatory = false,
                                                                                    interestsInSection,
                                                                                    fieldId
                                                                                  }: ResearcherInterestsProps<T>) {
  const {setValue, getValues, register, watch, trigger, formState: {errors}} = useFormContext<T>();

  const errorPath = get(errors, fieldId as Path<T>) as FieldError | undefined;

  // This is to be used when generating data-test-ids
  const fieldIdForDataTest = label.toLowerCase().replace(" ", "-");

  const selectionWatch: number[] = watch(fieldId) || [];

  return <Form.Group as={Row} className={"mb-2"} controlId={`formHorizontal${fieldIdForDataTest}`}>
    <Row>
      <Col sm={2}></Col>
      <Col sm={10} className={"question-help-text"}>
        {helpText}
        <span style={{fontStyle: "italic"}}> (Tick all that apply)</span>
      </Col>
    </Row>
    <Row>
      <Form.Label column sm={2}>
        {label} {mandatory && <MANDATORY_ICON/>}
      </Form.Label>
      <Col sm={10}>
        <Form.Control
            type="hidden"
            hidden
            isInvalid={!!errorPath}
            {...register(fieldId, {
              validate: (v) => v.length > 0 ? true : `${label} must have at least one selection`
            })}
        />
        {interestsInSection.map((interest, index) => (
            <Button
                key={`res-interest-section-${fieldIdForDataTest}-${index}`}
                type="button"
                variant={selectionWatch.includes(interest.interestId) ?
                    "primary" :
                    "outline-secondary"}
                style={{
                  marginRight: "10px",
                  marginBottom: "10px"
                }}

                data-testid={`${fieldIdForDataTest}-button`}
                onClick={() => {
                  const current = (getValues(fieldId) as number[]) || [];
                  const updated = current.includes(interest.interestId)
                      ? current.filter(id => id !== interest.interestId)
                      : [...current, interest.interestId];

                  setValue(fieldId, updated as PathValue<T, Path<T>>, {shouldValidate: true});
                }}>
              {interest.description}
            </Button>
        ))}
        <Form.Control.Feedback type="invalid" data-testid={`${fieldIdForDataTest}-error`}>
          {`This section must have at least one selection.`}
        </Form.Control.Feedback>
      </Col>
    </Row>
  </Form.Group>;
}